import Fuse from "fuse.js";
import { useMemo, useRef, useState } from "react";
import type { FormikProps } from "formik";
import { useFilteredCustomers, Customer } from "../queries/customer.queries";

interface CustomerSearchFormValues {
	buyer_name?: string;
	buyer_pan?: string;
	customer_mobile?: string;
}

interface PrefixFilter {
	customer_mobile: string;
	customer_name: string;
	customer_pan_vat: string;
	[key: string]: string;
}

// Buyer-name/mobile/PAN autocomplete for invoice-form. Previously dispatched
// a Redux thunk (searchCustomers) into customerReducer.customersList — moved
// onto useFilteredCustomers (the same one-shot search query customer-bulk-draft
// uses) fed a debounced 2-char-prefix filter, `enabled` only once one exists.
// React Query's own cache replaces the old manual lastAPIPrefixRef gate (a
// prefix typed once, cleared, then retyped now serves from cache instead of
// re-hitting the API). One deliberate simplification vs. the old two-mode
// behavior (raw batch right after a fresh fetch, Fuse-narrowed on subsequent
// same-prefix keystrokes): here every keystroke Fuse-searches the current
// input against the latest fetched batch, including the first — since the
// batch is already server-filtered to the 2-char prefix, this produces the
// same visible result without the extra branch.
export function useCustomerSearch<T extends CustomerSearchFormValues>(
	formik: FormikProps<T>
) {
	const [inputValue, setInputValue] = useState("");
	const [prefixFilter, setPrefixFilter] = useState<PrefixFilter | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const { data: customersBatch } = useFilteredCustomers(
		prefixFilter ?? {},
		prefixFilter !== null
	);

	const fuse = useMemo(() => {
		if (!customersBatch || customersBatch.length === 0) return null;
		return new Fuse(customersBatch, {
			shouldSort: true,
			includeScore: true,
			useExtendedSearch: true,
			keys: ["customer_name", "customer_mobile", "customer_pan_vat"],
		});
	}, [customersBatch]);

	const filteredClient: Customer[] = useMemo(() => {
		if (!inputValue.trim() || !fuse) return [];
		return fuse.search(inputValue.trim()).map((r) => r.item);
	}, [inputValue, fuse]);

	const handleCustomerInputSearch = (inputName: string, newInputValue: string) => {
		setInputValue(newInputValue);

		if (timeoutRef.current) clearTimeout(timeoutRef.current);

		timeoutRef.current = setTimeout(() => {
			const trimmed = newInputValue.trim();

			if (trimmed.length === 0) {
				setPrefixFilter(null);
				return;
			}

			const inputPrefix = trimmed.slice(0, 2).toLowerCase();

			setPrefixFilter({
				customer_mobile:
					inputName === "customer_mobile"
						? inputPrefix
						: (formik.values?.customer_mobile || "")
								.slice(0, 2)
								.toLowerCase(),
				customer_name:
					inputName === "buyer_name"
						? inputPrefix
						: (formik.values?.buyer_name || "")
								.slice(0, 2)
								.toLowerCase(),
				customer_pan_vat:
					inputName === "buyer_pan"
						? inputPrefix
						: (formik.values?.buyer_pan || "")
								.slice(0, 2)
								.toLowerCase(),
			});
		}, 500);
	};

	const handleCustomerInputBlur = () => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		setInputValue("");
		setPrefixFilter(null);
	};

	return {
		filteredClient,
		handleCustomerInputSearch,
		handleCustomerInputBlur,
	};
}
